import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trash2, Camera, Package, Home, Calendar, Tag, AlertTriangle, Plus } from 'lucide-react';
import { Item, Project, Room, Photo, DamageReport } from '../types';
import { getProject } from '../services/projectService';
import { getRoom } from '../services/roomService';
import { getItem, deleteItem } from '../services/itemService';
import { getPhotosByItem } from '../services/photoService';
import { getDamageReportsByItem } from '../services/damageReportService';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import PhotoViewer from '../components/PhotoViewer';
import ConfirmDialog from '../components/ConfirmDialog';
import { PageShell } from '../components/ui/PageShell';
import { SectionGroup } from '../components/ui/SectionGroup';
import { ActionRow } from '../components/ui/ActionRow';
import { StatusBadge } from '../components/ui/StatusBadge';
import { SkeletonList } from '../components/ui/Skeleton';

/**
 * Safely format a Firestore Timestamp to a Swedish locale string.
 * Legacy items may not have createdAt/updatedAt, or the field may arrive as
 * something other than a Timestamp (e.g. a raw object during hot-reload), so
 * guard every access.
 */
const formatTimestamp = (ts: unknown): string => {
  if (!ts || typeof ts !== 'object') return '—';
  const t = ts as { toDate?: () => Date };
  if (typeof t.toDate !== 'function') return '—';
  try {
    return t.toDate().toLocaleDateString('sv-SE', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return '—';
  }
};

const ItemDetailPage: React.FC = () => {
  const { projectId, itemId } = useParams<{ projectId: string; itemId: string }>();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();

  const [item, setItem] = useState<Item | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [room, setRoom] = useState<Room | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [damageReports, setDamageReports] = useState<DamageReport[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId || !itemId) {
        console.error('Missing required parameters:', { projectId, itemId });
        setError('Ogiltiga parametrar. Kontrollera URL:en och försök igen.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const [itemResult, projectResult, photosResult] = await Promise.allSettled([
          getItem(itemId),
          getProject(projectId),
          getPhotosByItem(projectId, itemId),
        ]);

        if (itemResult.status === 'rejected') {
          console.error('Error fetching item:', itemResult.reason);
          setError('Objektet kunde inte hittas. Kontrollera att objektet existerar.');
          setLoading(false);
          return;
        }
        const fetchedItem = itemResult.value;
        setItem(fetchedItem);

        if (projectResult.status === 'rejected') {
          console.error('Error fetching project:', projectResult.reason);
          setError('Projektet kunde inte hittas. Kontrollera att projektet existerar.');
          setLoading(false);
          return;
        }
        setProject(projectResult.value);

        setPhotos(photosResult.status === 'fulfilled' ? photosResult.value : []);

        // Fetch damage reports for this item
        try {
          const reports = await getDamageReportsByItem(projectId, itemId);
          setDamageReports(reports);
        } catch {
          // Non-critical, continue without damage reports
        }

        if (fetchedItem.roomId) {
          try {
            const fetchedRoom = await getRoom(fetchedItem.roomId);
            setRoom(fetchedRoom);
          } catch {
            setRoom(null);
          }
        } else {
          setRoom(null);
        }
      } catch (err) {
        console.error('Unexpected error in fetchData:', err);
        setError('Ett oväntat fel inträffade. Försök igen senare.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, itemId]);

  const handleDelete = () => {
    if (!itemId) return;
    if (!isOnline) {
      setError('Du är offline. Anslut till internet för att ta bort objektet.');
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!itemId || !projectId) return;
    setShowDeleteConfirm(false);
    try {
      setDeleting(true);
      await deleteItem(itemId, projectId);
      navigate(item?.roomId
        ? `/projects/${projectId}/rooms/${item.roomId}`
        : `/projects/${projectId}/rooms`
      );
    } catch (err) {
      console.error('Error deleting item:', err);
      setError('Kunde inte ta bort objektet. Försök igen senare.');
      setDeleting(false);
    }
  };

  const backLink = item?.roomId
    ? `/projects/${projectId}/rooms/${item.roomId}`
    : `/projects/${projectId}/rooms`;

  const editLink = `/projects/${projectId}/items/${itemId}/edit`;

  if (loading) {
    return (
      <PageShell title="Laddar..." backTo={backLink} backLabel="Objekt">
        <div className="space-y-4 py-2">
          <SkeletonList count={4} />
          <SkeletonList count={2} />
        </div>
      </PageShell>
    );
  }

  if (error || !item || !project) {
    return (
      <PageShell title="Objektdetaljer" backTo={projectId ? backLink : '/'} backLabel="Objekt">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm mt-4">
          {error || 'Objektet kunde inte hittas.'}
          {!isOnline && <p className="mt-1 text-xs">Du är offline.</p>}
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={item.name}
      backTo={backLink}
      backLabel="Objekt"
      headerRight={
        <button
          onClick={() => navigate(editLink)}
          className="text-accent-600 dark:text-accent-400 text-[17px] active:opacity-50 transition-opacity"
        >
          Redigera
        </button>
      }
    >
      <div className="space-y-4 pb-24">
        {/* Item header accent card */}
        <div className="bg-accent-50 dark:bg-accent-900/20 border border-accent-200 dark:border-accent-800/40 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-accent-100 dark:bg-accent-900/40 flex items-center justify-center flex-shrink-0">
                <Package size={22} className="text-accent-600 dark:text-accent-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-accent-800 dark:text-accent-300 mb-0.5">{item.name}</p>
                <div className="flex items-center text-[15px] text-accent-700 dark:text-accent-400">
                  <Home size={16} className="mr-1" />
                  <span>{room ? room.name : (item.roomId ? 'Rum kunde inte laddas' : 'Inget rum')}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <StatusBadge status={item.priority} />
              <StatusBadge status={item.pickupStatus} />
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm">
            {error}
          </div>
        )}

        {/* Item details */}
        <SectionGroup title="Objektdetaljer">
          {item.category && (
            <ActionRow
              label="Kategori"
              value={item.category}
              chevron={false}
            />
          )}
          {item.estimatedValue !== undefined && (
            <ActionRow
              label="Uppskattat värde"
              value={`${item.estimatedValue} kr`}
              chevron={false}
            />
          )}
          {item.description && (
            <div className="px-4 py-3 border-b border-[var(--border-subtle)]">
              <p className="text-sm text-[var(--text-tertiary)] font-medium mb-1">Beskrivning</p>
              <p className="text-[15px] text-[var(--text-primary)] whitespace-pre-wrap leading-relaxed">{item.description}</p>
            </div>
          )}
          {item.notes && (
            <div className="px-4 py-3 border-b border-[var(--border-subtle)]">
              <p className="text-sm text-[var(--text-tertiary)] font-medium mb-1">Anteckningar</p>
              <p className="text-[15px] text-[var(--text-primary)] whitespace-pre-wrap leading-relaxed">{item.notes}</p>
            </div>
          )}
          {item.pickupNotes && (
            <div className="px-4 py-3">
              <p className="text-sm text-[var(--text-tertiary)] font-medium mb-1">Upphämtning</p>
              <p className="text-[15px] text-[var(--text-primary)] whitespace-pre-wrap leading-relaxed">{item.pickupNotes}</p>
            </div>
          )}
        </SectionGroup>

        {/* Tags */}
        {item.tags && item.tags.length > 0 && (
          <SectionGroup title="Taggar">
            <div className="px-4 py-3 flex flex-wrap gap-1.5">
              {item.tags.map((tag, index) => (
                <span key={index} className="bg-[var(--bg-surface-alt)] text-[var(--text-primary)] px-3 py-1.5 rounded-full text-sm border border-[var(--border-default)] font-medium">
                  {tag}
                </span>
              ))}
            </div>
          </SectionGroup>
        )}

        {/* Metadata */}
        <SectionGroup title="Metadata">
          <ActionRow
            icon={Calendar}
            label="Skapad"
            value={formatTimestamp(item.createdAt)}
            chevron={false}
          />
          <ActionRow
            icon={Calendar}
            label="Uppdaterad"
            value={formatTimestamp(item.updatedAt)}
            chevron={false}
          />
        </SectionGroup>

        {/* Photos */}
        <SectionGroup title={`Foton (${photos.length})`}>
          {photos.length === 0 ? (
            <div className="px-4 py-4 text-center">
              <Camera size={24} className="text-[var(--text-tertiary)] mx-auto mb-2" />
              <p className="text-[15px] text-[var(--text-tertiary)]">Inga foton att visa</p>
              <p className="text-sm text-[var(--text-tertiary)] mt-1">Redigera objektet för att lägga till foton</p>
            </div>
          ) : (
            <div className="p-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {photos.map((photo, index) => (
                  <div
                    key={photo.id}
                    className="relative aspect-square overflow-hidden rounded-lg border border-[var(--border-default)] cursor-pointer active:opacity-80 transition-opacity shadow-sm"
                    onClick={() => setSelectedPhotoIndex(index)}
                  >
                    <img
                      src={photo.thumbnail}
                      alt={`Foto ${photo.id}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </SectionGroup>

        {/* Damage Reports */}
        <SectionGroup title={`Skador (${damageReports.length})`}>
          {damageReports.length > 0 ? (
            <div className="divide-y divide-[var(--border-subtle)]">
              {damageReports.map(report => (
                <div
                  key={report.id}
                  onClick={() => navigate(`/projects/${projectId}/damage-reports/${report.id}`)}
                  className="px-4 py-3 press-row cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2.5 flex-1 min-w-0">
                      <AlertTriangle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-[var(--text-primary)] line-clamp-2">{report.description}</p>
                        <p className="text-xs text-[var(--text-secondary)] mt-0.5">{report.reportedBy}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[12px] font-medium ml-2 flex-shrink-0 ${
                      report.status === 'open' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800/40' :
                      report.status === 'in-progress' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800/40' :
                      'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/40'
                    }`}>
                      {report.status === 'open' ? 'Öppen' : report.status === 'in-progress' ? 'Pågående' : 'Löst'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-4 py-3 text-center">
              <p className="text-sm text-[var(--text-tertiary)]">Inga skaderapporter</p>
            </div>
          )}
          <ActionRow
            icon={Plus}
            iconColor="text-emerald-500"
            label="Ny skaderapport"
            to={`/projects/${projectId}/items/${itemId}/damage-reports/new`}
          />
        </SectionGroup>

        {/* Delete */}
        <SectionGroup>
          <ActionRow
            icon={Trash2}
            label={deleting ? 'Tar bort...' : 'Ta bort objekt'}
            onClick={handleDelete}
            destructive={true}
            chevron={false}
          />
        </SectionGroup>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Ta bort"
        message="Är du säker på att du vill ta bort detta objekt? Denna åtgärd kan inte ångras."
        confirmLabel="Ta bort"
        cancelLabel="Avbryt"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      {/* Photo Viewer */}
      {selectedPhotoIndex !== null && (
        <PhotoViewer
          photos={photos}
          initialPhotoIndex={selectedPhotoIndex}
          onClose={() => setSelectedPhotoIndex(null)}
        />
      )}
    </PageShell>
  );
};

export default ItemDetailPage;
